import { useEffect, useRef } from 'react';
import { parseMoney } from '../utils/clickerMerge';
import { getOutstandingTaps, LOCAL_STORAGE_KEY_ENERGY, isTapFlushing } from '../utils/tapFlush';

function readStoredEnergy(fallback = NaN) {
  try {
    return parseMoney(localStorage.getItem(LOCAL_STORAGE_KEY_ENERGY), fallback);
  } catch (_) {
    return fallback;
  }
}

/**
 * Global energy regen — same 1/s tick as Main, but lives in AppLayout so energy
 * keeps filling on Build / Season / Hub / etc.
 *
 * Base = optimistic LS (or clicker energy). Never subtract outstanding taps from
 * an already-spent meter — that double-debit zeros the bar in a few clicks.
 */
export function useGlobalEnergyRegen(clickerData, setClickerData) {
  const clickerRef = useRef(clickerData);
  useEffect(() => {
    clickerRef.current = clickerData;
  }, [clickerData]);

  useEffect(() => {
    if (!setClickerData) return undefined;

    // Тик энергии тоже не должен будить телефон в фоне. Внутренняя проверка
    // видимости раньше только пропускала работу — сам таймер всё равно
    // срабатывал раз в секунду круглые сутки.
    let id = 0;
    const stop = () => {
      if (id) window.clearInterval(id);
      id = 0;
    };
    const step = () => {
      if (isTapFlushing()) return;

      const cur = clickerRef.current;
      if (!cur) return;

      const maxE = parseMoney(cur.max_energy, NaN);
      const rate = parseMoney(cur.energy_rate, NaN);
      if (!(Number.isFinite(maxE) && maxE > 0)) return;
      if (!(Number.isFinite(rate) && rate > 0)) return;

      const raw = parseMoney(cur.energy, NaN);
      const lsEn = readStoredEnergy(NaN);
      const outstanding = getOutstandingTaps();

      let energy;
      if (Number.isFinite(lsEn)) {
        // LS is the live meter (taps + regen). While taps are queued, never
        // raise above LS toward a stale high React value.
        energy = outstanding > 0 && Number.isFinite(raw) ? Math.min(lsEn, raw) : lsEn;
        if (outstanding <= 0 && Number.isFinite(raw)) {
          energy = Math.max(raw, lsEn);
        }
      } else if (Number.isFinite(raw)) {
        energy = raw;
      } else {
        return;
      }

      if (energy >= maxE - 1e-6) return;

      const next = Math.min(maxE, energy + rate);
      if (next <= energy + 1e-9) return;

      try {
        localStorage.setItem(LOCAL_STORAGE_KEY_ENERGY, next.toFixed(2));
      } catch (_) {
        /* ignore */
      }

      setClickerData((prev) => {
        if (!prev) return prev;
        const prevEn = parseMoney(prev.energy, NaN);
        const safeBase =
          outstanding > 0 && Number.isFinite(prevEn) && prevEn > energy + 1e-6
            ? energy
            : Number.isFinite(prevEn)
              ? prevEn
              : energy;
        if (safeBase >= maxE - 1e-6) return prev;
        const merged = Math.min(maxE, safeBase + rate);
        if (Math.abs(merged - safeBase) < 1e-9) return prev;
        return { ...prev, energy: String(merged) };
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Пока приложение было свёрнуто, энергия копилась на сервере — здесь
        // достаточно возобновить тик, обгонять сервер локально не нужно.
        if (!id) id = window.setInterval(step, 1000);
      } else {
        stop();
      }
    };

    if (document.visibilityState === 'visible') id = window.setInterval(step, 1000);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [setClickerData]);
}
